/**
 * CSRF Protection Utility
 * Modern ES6 module for client-side CSRF token management
 * 
 * Features:
 * - Automatic token fetching and refresh
 * - Secure request helpers with built-in CSRF protection
 * - Form submission utilities
 * - JWT integration
 * - Global API compatibility
 * 
 * @author Zubduq 
 * @version 2.0.0
 */

/**
 * CSRF Manager Class
 * Handles all CSRF token operations and secure requests
 */
class CSRFManager {
  constructor() {
    this.token = null;
    this.tokenExpiry = null;
    this.tokenRefreshInterval = null;
    this.isInitialized = false;

    // Bind methods to preserve 'this' context
    this.refreshToken = this.refreshToken.bind(this);
    this.getToken = this.getToken.bind(this);
    this.secureFetch = this.secureFetch.bind(this);
    this.secureFormSubmit = this.secureFormSubmit.bind(this);
  }

  /**
   * Initialize the CSRF manager
   */
  async init() {
    if (this.isInitialized) return;

    try {
      // Fetch initial token
      await this.refreshToken();
      
      // Set up automatic token refresh every 30 minutes
      this.startTokenRefresh();
      
      this.isInitialized = true;
    } catch (error) {
      // Silent initialization failure
    }
  }

  /**
   * Fetch a fresh CSRF token from the server
   */
  async refreshToken() {
    try {
      const response = await fetch("/api/csrf-token", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: this.getAuthHeader(),
        },
      });

      if (response.ok) {
        const data = await response.json();
        this.token = data.csrfToken;
        this.tokenExpiry = Date.now() + 23 * 60 * 60 * 1000; // 23 hours

        return this.token;
      } else {
        this.token = null;
        return null;
      }
    } catch (error) {
      this.token = null;
      return null;
    }
  }

  /**
   * Get current token, refresh if needed
   */
  async getToken() {
    // If no token or token expired, refresh it
    if (!this.token || (this.tokenExpiry && Date.now() > this.tokenExpiry)) {
      await this.refreshToken();
    }

    return this.token;
  }

  /**
   * Get authorization header for JWT
   */
  getAuthHeader() {
    const jwtToken = localStorage.getItem("jwtToken");
    return jwtToken ? `Bearer ${jwtToken}` : "";
  }

  /**
   * Add CSRF token to request headers
   */
  async getSecureHeaders(additionalHeaders = {}) {
    const csrfToken = await this.getToken();

    const headers = {
      Authorization: this.getAuthHeader(),
      ...additionalHeaders,
    };

    // Only add Content-Type if not explicitly set to null (for FormData uploads)
    if (additionalHeaders['Content-Type'] !== null) {
      if (!headers['Content-Type']) {
        headers["Content-Type"] = "application/json";
      }
    } else {
      // Remove Content-Type if explicitly set to null
      delete headers['Content-Type'];
    }

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    return headers;
  }

  /**
   * Enhanced fetch with automatic CSRF token inclusion and JWT refresh
   */
  async secureFetch(url, options = {}) {
    // Skip CSRF for GET requests
    if (!options.method || options.method.toLowerCase() === "get") {
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: this.getAuthHeader(),
        },
      });
    }

    // Helper function to make the actual request
    const makeRequest = async (retryCount = 0) => {
      try {
        // Detect if body is FormData (for file uploads)
        const isFormData = options.body instanceof FormData;
        
        // Add CSRF token for POST, PUT, DELETE, etc.
        // For FormData, don't add Content-Type header (browser will set it with boundary)
        const secureHeaders = isFormData 
          ? await this.getSecureHeaders({ 'Content-Type': null })
          : await this.getSecureHeaders(options.headers);

        const response = await fetch(url, {
          ...options,
          headers: secureHeaders,
        });

        // If 401 (JWT expired) and we haven't retried yet, attempt token refresh
        if (response.status === 401 && retryCount === 0) {
          const responseClone = response.clone();
          const errorData = await responseClone.json().catch(() => ({}));
          
          if (errorData.expired) {
            console.log('JWT token expired, attempting refresh...');
            
            // Try to refresh JWT token if refresh token exists
            const refreshToken = localStorage.getItem("refreshToken");
            if (refreshToken) {
              try {
                const refreshResponse = await fetch("/api/refresh-token", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ refreshToken }),
                });

                if (refreshResponse.ok) {
                  const refreshData = await refreshResponse.json();
                  localStorage.setItem("jwtToken", refreshData.token);
                  localStorage.setItem("refreshToken", refreshData.refreshToken);
                  
                  // Refresh CSRF token with new JWT
                  await this.refreshToken();
                  
                  // Retry the original request
                  return makeRequest(retryCount + 1);
                }
              } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
              }
            }
            
            // If refresh fails, redirect to login
            console.log('Token refresh failed, redirecting to login...');
            window.location.href = '/auth/login.html';
            return response;
          }
        }

        // If 403 (CSRF token invalid) and we haven't retried yet, refresh CSRF token
        if (response.status === 403 && retryCount === 0) {
          const responseClone = response.clone();
          const errorData = await responseClone.json().catch(() => ({}));
          
          if (errorData.code === 'CSRF_TOKEN_INVALID') {
            console.log('CSRF token invalid, refreshing...');
            
            // Force refresh CSRF token
            this.token = null;
            await this.refreshToken();
            
            // Retry the original request
            return makeRequest(retryCount + 1);
          }
        }

        // If 429 (Rate Limited) and we haven't retried yet, implement exponential backoff
        if (response.status === 429 && retryCount === 0) {
          const responseClone = response.clone();
          const errorData = await responseClone.json().catch(() => ({}));
          console.log('Rate limit exceeded, implementing backoff...');
          
          // Get retry-after header or use default backoff
          const retryAfter = response.headers.get('retry-after');
          const backoffMs = retryAfter ? parseInt(retryAfter) * 1000 : Math.min(2000 * Math.pow(2, retryCount), 10000);
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, backoffMs));
          
          // Retry the original request
          return makeRequest(retryCount + 1);
        }

        return response;
      } catch (error) {
        // Network errors or other issues
        throw error;
      }
    };

    return makeRequest();
  }

  /**
   * Helper for form submissions with CSRF protection
   */
  async secureFormSubmit(formData, url, options = {}) {
    const csrfToken = await this.getToken();

    // Add CSRF token to form data
    if (csrfToken) {
      if (formData instanceof FormData) {
        formData.append("_csrf", csrfToken);
      } else if (typeof formData === "object") {
        formData._csrf = csrfToken;
      }
    }

    // For FormData (file uploads), don't use secureFetch as it sets JSON headers
    if (formData instanceof FormData) {
      const headers = {
        Authorization: this.getAuthHeader(),
        ...options.headers,
      };
      // Don't set Content-Type for FormData - browser will set it with boundary

      return fetch(url, {
        method: "POST",
        body: formData,
        headers,
        ...options,
      });
    } else {
      // For JSON data, use secureFetch
      return this.secureFetch(url, {
        method: "POST",
        body: JSON.stringify(formData),
        ...options,
      });
    }
  }

  /**
   * Start automatic token refresh with JWT expiry awareness
   */
  startTokenRefresh() {
    // Refresh CSRF token every 30 minutes
    this.tokenRefreshInterval = setInterval(
      () => {
        this.refreshToken();
      },
      30 * 60 * 1000,
    );

    // Check JWT expiry and refresh proactively every 5 minutes
    this.jwtCheckInterval = setInterval(
      () => {
        this.checkJWTExpiry();
      },
      5 * 60 * 1000,
    );
  }

  /**
   * Check if JWT is close to expiry and refresh if needed
   */
  async checkJWTExpiry() {
    try {
      const jwtToken = localStorage.getItem("jwtToken");
      if (!jwtToken) return;

      // Decode JWT to check expiry
      const payload = JSON.parse(atob(jwtToken.split(".")[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;

      // If token expires in less than 10 minutes, try to refresh
      if (timeUntilExpiry < 10 * 60 * 1000 && timeUntilExpiry > 0) {
        console.log('JWT expires soon, attempting proactive refresh...');
        
        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
          try {
            const refreshResponse = await fetch("/api/refresh-token", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ refreshToken }),
            });

            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              localStorage.setItem("jwtToken", refreshData.token);
              localStorage.setItem("refreshToken", refreshData.refreshToken);
              
              // Refresh CSRF token with new JWT
              await this.refreshToken();
              
              console.log('JWT and CSRF tokens refreshed proactively');
            }
          } catch (error) {
            console.error('Proactive JWT refresh failed:', error);
          }
        }
      }
    } catch (error) {
      // Silent failure for JWT expiry checks
    }
  }

  /**
   * Stop automatic token refresh
   */
  stopTokenRefresh() {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
    if (this.jwtCheckInterval) {
      clearInterval(this.jwtCheckInterval);
      this.jwtCheckInterval = null;
    }
  }

  /**
   * Clean up when user logs out
   */
  cleanup() {
    this.stopTokenRefresh();
    this.token = null;
    this.tokenExpiry = null;
    this.isInitialized = false;
  }

  /**
   * Get status information
   */
  getStatus() {
    return {
      hasToken: !!this.token,
      tokenExpiry: this.tokenExpiry,
      isExpired: this.tokenExpiry && Date.now() > this.tokenExpiry,
      isInitialized: this.isInitialized,
      refreshInterval: !!this.tokenRefreshInterval
    };
  }
}

// Create singleton instance
const csrfManager = new CSRFManager();

// Note: Initialization is now handled by the main application (main.js)
// to avoid double initialization and ensure proper timing with other components

// Named exports for ES6 module usage
export { csrfManager as CSRFManager };
export const refreshToken = () => csrfManager.refreshToken();
export const getToken = () => csrfManager.getToken();
export const secureFetch = (url, options) => csrfManager.secureFetch(url, options);
export const secureFormSubmit = (formData, url, options) => csrfManager.secureFormSubmit(formData, url, options);
export const getSecureHeaders = (additionalHeaders) => csrfManager.getSecureHeaders(additionalHeaders);
export const cleanup = () => csrfManager.cleanup();

// Global API for backward compatibility
window.csrfClient = csrfManager;
window.secureFetch = secureFetch;
window.secureFormSubmit = secureFormSubmit;

// Expose CSRF object for consistency with other components
window.CSRF = {
  manager: csrfManager,
  refreshToken,
  getToken,
  secureFetch,
  secureFormSubmit,
  getSecureHeaders,
  cleanup,
  getStatus: () => csrfManager.getStatus()
};

export default csrfManager;
